/**
 * The demo backend's binary payloads.
 *
 * Base64 bytes for the things the mock transport serves over its media route: three profile
 * photos, an identity document as an image and another as a PDF, and two bill-service icons.
 * They live in their own file rather than in `data.ts` because they are large, opaque and
 * never read by a person, and burying the seed data underneath thirty lines of base64 makes
 * the seeds harder to edit than they need to be.
 *
 * Every one is generated rather than sourced: a real face, a real identity slip or a real
 * biller's logo has no business in a repository, and a demo backend that shipped one would be
 * a demo backend that leaked something. The photos are a plain silhouette, the documents say
 * on their face that they are samples, and the icons are flat marks.
 *
 * All raster payloads are PNG. That is the same rule the console enforces on upload and the
 * contract enforces on avatars and brand assets: no SVG anywhere these bytes could be
 * rendered, because an SVG is an active document that runs when it is displayed.
 */

/** A 48x48 silhouette on a soft violet field. */
export const DEMO_AVATAR_VIOLET =
  'iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAIAAADYYG7QAAAAlUlEQVR42u3WwRGAMAgEQEq1FMuzCJ9W4csC1EAIEMjczBWw' +
  'D+Y4us47VQgggAACCCBb0L4d78wBfVLGWeSkUZuWAAk1OlN9UJdGYQIIRw1Q0qZO98tY08w9ZLg9lhhouYpRoek1UYCmy0Qx' +
  'GrmpJshEIzRRpEZiqgYy17CmUiAnTdsEkBrkqmmYAALIBxSg+TMBxIEeZpUnNq44NX0AAAAASUVORK5CYII=';

/** The same silhouette, green. */
export const DEMO_AVATAR_GREEN =
  'iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAIAAADYYG7QAAAAlklEQVR42u3W0Q2AMAgEUMZwRgdyNGdwAAdwALVQChSaS26A' +
  '90GOo/O+UoUAAggggACyBW3H/s4c0CdlnEVOGrVpCZBQozPVB3VpFCaAcNQAJW3qdL+MNc3cQ4bbY4mBlqsYFZpeEwVoukwU' +
  'o5GbaoJMNEITRWokpmogcw1rKgVy0rRNAKlBrpqGCSCAfEABmj8TQBzoAXEjMEC3Fad/AAAAAElFTkSuQmCC';

/** The same silhouette, amber. */
export const DEMO_AVATAR_AMBER =
  'iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAIAAADYYG7QAAAAlklEQVR42u3W0Q2AMAgEUAZyPid0BmdwCD8cQC2UAoXmkhvg' +
  'fZDj6L7OVCGAAAIIIIBsQce+vTMH9EkZZ5GTRm1aAiTU6Ez1QV0ahQkgHDVASZs63S9jTTP3kOH2WGKg5SpGhabXRAGaLhPF' +
  'aOSmmiATjdBEkRqJqRrIXMOaSoGcNG0TQGqQq6ZhAgggH1CA5s8EEAd6APXxVhE8A1/rAAAAAElFTkSuQmCC';

/** A 260x170 mock of a scanned identity card: photo box, four text lines, an MRZ band. */
export const DEMO_KYC_SCAN =
  'iVBORw0KGgoAAAANSUhEUgAAAQQAAACqCAIAAADEC6Z3AAAC1UlEQVR42u3dO04rQRRFUQ/bA2AMhBZDQATMwKkjRERE5ICQ' +
  'CMuSHVkIIfevuu+triXt7J3I9ZYI+rd5ft1LurbxE0gwSDBInRh+TmepqWCQYJBgkGCQYJBgkGCQYJBgkGCQYJBgkGCQYJBg' +
  'kGCQYJBgkGCQasbw/nGsMectGGAQDDAIBhgEAwyCAQbBMBOGx92Lag8GGAQDDIIBBsEAg2CAQTDAoFYwbB+eYFCjGK7/++/n' +
  'opvWf9Gtk0FxEs5b6TAMYlCQhPNWLgyjJUz34LyVCMNECRM9OG9lwVBEwhQPzlswwKBMGApKGO3BeQsGGJQGQ3EJ4zw4b8Hg' +
  'doz130wBAwwwwAADDDDAAEPFGGaSMMIDDDD4ywADDDDAAAMMMMAAg4tuggEGuTcJBsEAgzzP4HkGwQCDPAPtGWh5O4a3Y8h7' +
  'k7w3Sd6o5416avWbbmnftepuiLpupljhBw69hRsGGHyfAQYYYIABBhhggAEGGGCAAQYYYIABhooxyEU3d60KBhgEgyfdJC8E' +
  'kGCQvGtVggEGRWOYVcJQD85bMMCgaAwLSBjkwe0YK76ZAgYYYKgBw2IS+nuAAQYYYIABBhhgCMewsISeHmCAAQYYYIABBhhi' +
  'MYRI6OPBdSXBAINggEGBGAIldHpw3oIBBsEAg2CAQTDAoBwYwiXc9+C8BQMMggEGwQCDYIBBy2KQYJBgkGCQYPDrCAYYBAMM' +
  'ggEGCQYJBgkGCQYJBgkGCQYJBgkGKQjD4e3z1vHr+0+//9XGJs8GBhsbGGxsYLCxgcHGBgYbGxhsbGCwsYHBxgYGGxsYbGxg' +
  'sLGBwcYGBhsbGGxsYLCxgcHGBgYbGxhsbGCwsYHBxgYGB2ADgwOwgcEB2MDgAGxg8OPawACDDQww2MAAgw0MMNjAAIMNDL7P' +
  'IN9ngEEwwCDBIMEgwSDBIMEgwSDBIMEgwSDBIM2PQWo5GCQYJBikf7sAC7DLt8p4dcYAAAAASUVORK5CYII=';

/** A one-page PDF slip, so the document viewer's PDF branch is demoable rather than theoretical. */
export const DEMO_KYC_PDF =
  'JVBERi0xLjQKMSAwIG9iago8PCAvVHlwZSAvQ2F0YWxvZyAvUGFnZXMgMiAwIFIgPj4KZW5kb2JqCjIgMCBvYmoKPDwgL1R5' +
  'cGUgL1BhZ2VzIC9LaWRzIFszIDAgUl0gL0NvdW50IDEgPj4KZW5kb2JqCjMgMCBvYmoKPDwgL1R5cGUgL1BhZ2UgL1BhcmVu' +
  'dCAyIDAgUiAvTWVkaWFCb3ggWzAgMCA0MjAgMzAwXSAvQ29udGVudHMgNCAwIFIgL1Jlc291cmNlcyA8PCAvRm9udCA8PCAv' +
  'RjEgNSAwIFIgPj4gPj4gPj4KZW5kb2JqCjQgMCBvYmoKPDwgL0xlbmd0aCAyNDcgPj4Kc3RyZWFtCkJUIC9GMSAxMyBUZiA0' +
  'MCAyNTAgVGQgMTYgVEwKKEdlbkV4IFBheSAtIHNhbXBsZSBpZGVudGl0eSBzbGlwKSBUaiBUKgooKSBUaiBUKgooTmFtZTog' +
  'QWlzaGEgRGFubGFkaSkgVGogVCoKKE5JTjogMjE0NTg4OTkwMzEpIFRqIFQqCihEYXRlIG9mIGJpcnRoOiAxOTk0LTAzLTE1' +
  'KSBUaiBUKgooSXNzdWVkOiAyMDIzLTExLTAyKSBUaiBUKgooKSBUaiBUKgooRGVtbyBkb2N1bWVudC4gTm90IGEgcmVhbCBy' +
  'ZWNvcmQuKSBUaiBUKgpFVAplbmRzdHJlYW0KZW5kb2JqCjUgMCBvYmoKPDwgL1R5cGUgL0ZvbnQgL1N1YnR5cGUgL1R5cGUx' +
  'IC9CYXNlRm9udCAvSGVsdmV0aWNhID4+CmVuZG9iagp4cmVmCjAgNgowMDAwMDAwMDAwIDY1NTM1IGYgCjAwMDAwMDAwMDkg' +
  'MDAwMDAgbiAKMDAwMDAwMDA1OCAwMDAwMCBuIAowMDAwMDAwMTE1IDAwMDAwIG4gCjAwMDAwMDAyNDEgMDAwMDAgbiAKMDAw' +
  'MDAwMDUzOCAwMDAwMCBuIAp0cmFpbGVyCjw8IC9TaXplIDYgL1Jvb3QgMSAwIFIgPj4Kc3RhcnR4cmVmCjYwOAolJUVPRgo=';

/** A 64x64 lightning mark, for the electricity biller. */
export const DEMO_ICON_BOLT =
  'iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAIAAAAlC+aJAAAAzElEQVR42u3YQQrEQAgEQF+wr80j86vdQyCEPQRC1O4ee/Ai' +
  'MgOleJn4bPsR33fnfKc54pooGjIBEEP85fIAOcOKAC3DogAhw7oAFUMhoMcQ92V5AL9hAIDcMAPAbBgDoDVMAnAaWgEVhnh6' +
  'QR7AZhgJoDJMBfAYBgNIDEhAiiFS2gBkUADeGCJrmeQBKIMBaIMBaIMBaAMj4JEhin475AFtBgPQBgPQBgPQBgPQBg3AjaEJ' +
  'UDeEPkCRwQC0wQC0wQC0QRJwNWAAiUP4AZsgmwxDqOdvAAAAAElFTkSuQmCC';

/** A 64x64 flat navy tile, for the television biller. */
export const DEMO_ICON_TV =
  'iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAIAAAAlC+aJAAAAUUlEQVR42u3PQQkAAAgEsEvh0yT2z2UE38JgBZbqeS0CAgIC' +
  'AgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAldgAbXJAGo9O1yeAAAAAElFTkSuQmCC';
